import React from "react";
import {
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { EXPORT_INCLUSION_TYPES, type ExportFieldConfiguration } from "./types";
import { getCommonExportRatioOptions } from "./utils";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import {
  contactsExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";

const DATA_TYPE = "Contacts";

// Matches OppGen's default once the "Limit contacts per company" checkbox is enabled.
const DEFAULT_CONTACTS_PER_COMPANY_LIMIT = 20;

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  fieldConfiguration: ExportFieldConfiguration;
  setFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  contactsPerCompanyLimit: number | null;
  setContactsPerCompanyLimit: (limit: number | null) => void;
}

export const ContactsExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  fieldConfiguration,
  setFieldConfiguration,
  contactsPerCompanyLimit,
  setContactsPerCompanyLimit,
}) => {
  const isDisabled = inclusion === EXPORT_INCLUSION_TYPES.NONE;
  const limitEnabled = contactsPerCompanyLimit !== null;

  const handleLimitEnabledChange = () => {
    setContactsPerCompanyLimit(
      limitEnabled ? null : DEFAULT_CONTACTS_PER_COMPANY_LIMIT
    );
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactsPerCompanyLimit(Math.max(1, Number(e.target.value)));
  };

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Stack>
        <Typography variant="h5" fontWeight={500} fontSize={16}>
          Contacts
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Configure which contacts to include in the export. Contact filters are
          set in the filter sidebar.
        </Typography>
      </Stack>
      <ExportDataTypeInclusionSelector
        dataType={DATA_TYPE}
        options={getCommonExportRatioOptions(DATA_TYPE)}
        inclusion={inclusion}
        setInclusion={setInclusion}
      />
      <Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={limitEnabled}
              onChange={handleLimitEnabledChange}
              disabled={isDisabled}
              data-testid="contacts-limit-per-company-checkbox"
            />
          }
          label="Limit contacts per company"
        />
        {limitEnabled && (
          <Stack sx={{ pl: 1.5, pt: 1 }} spacing={0.5}>
            <TextField
              label="Max per company"
              type="number"
              size="small"
              value={contactsPerCompanyLimit ?? ""}
              onChange={handleLimitChange}
              disabled={isDisabled}
              inputProps={{
                min: 1,
                "data-testid": "contacts-per-company-limit-input",
              }}
              sx={{ maxWidth: 200 }}
            />
            <Typography variant="caption" color="text.secondary">
              Caps the number of contacts exported per unique company domain.
            </Typography>
          </Stack>
        )}
      </Stack>
      <FieldConfigurationSelector
        fields={contactsExportFields}
        firmographicsFields={firmographicsExportFields}
        value={fieldConfiguration}
        onChange={setFieldConfiguration}
        title="Contacts Fields"
        checkboxLabel="Export All Contact Fields"
        disabled={isDisabled}
      />
    </Stack>
  );
};
